// Formulir Pelanggan vs Mockup sebelum FIX. Hanya membaca hasil pemeriksaan tersimpan; AI dipicu backend sekali per kombinasi versi.
import axios from "axios";
import { useCallback, useEffect, useState } from "react";
import { AlertTriangle, CheckCircle2, Loader2 } from "lucide-react";
import { DesignFormButton } from "./design_form";
import { getChannel } from "./channel";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/form-mockup-check`;
export const fetchFormCheck = (jobId) => axios.get(`${API}/job/${jobId}`, { withCredentials: true }).then(r => r.data);
export const needsFixReason = (st) => !st || !["sesuai", "off"].includes(st.state);
export const formCheckSummary = (st) => [st?.label || "Pemeriksaan tidak tersedia", ...(st?.diffs || []).map(d => `• ${d.msg}`),
  ...(st?.checks || []).map(d => `• Perlu dicek: ${d.msg}`), ...(st?.notes || []).map(n => `• ${n}`)].join("\n");
const mockSig = (job) => (job?.mockups || []).filter(m => !m.deleted_at).map(m => `${m.id}:${m.current_version}`).join(",");

export function useFormCheck(job) {
  const [st, setSt] = useState(null);
  const id = job?.id;
  const sig = mockSig(job);
  const load = useCallback(() => (id ? fetchFormCheck(id).then(setSt).catch(() => setSt(null)) : Promise.resolve()), [id, sig]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (st?.state !== "pending") return undefined;
    const t = setTimeout(load, 8000);
    return () => clearTimeout(t);
  }, [st, load]);
  return [st, load];
}

function CheckStatus({ st }) {
  if (!st) return <div className="muted small" data-testid="fmc-status">Memuat pemeriksaan formulir…</div>;
  const ok = st.state === "sesuai";
  const Icon = ok ? CheckCircle2 : st.state === "pending" ? Loader2 : AlertTriangle;
  return <div className={ok ? "small" : "warn-box small"} style={{ margin: 0 }} data-testid="fmc-status" data-state={st.state}>
    <b><Icon size={13} className={st.state === "pending" ? "spin" : undefined}/> Formulir vs Mockup: {st.label}</b>
    {st.form && <span className="muted" data-testid="fmc-source"> · Acuan: {st.form.source === "chat" ? "Teks chat" : "Formulir"} v{st.form.version}</span>}
    {(st.diffs || []).length > 0 && <ul style={{ margin: "4px 0 0 16px", padding: 0 }} data-testid="fmc-diffs">
      {st.diffs.map((d, i) => <li key={i} data-testid={`fmc-diff-${i}`}>{d.msg}</li>)}</ul>}
    {(st.checks || []).length > 0 && <ul style={{ margin: "4px 0 0 16px", padding: 0 }} data-testid="fmc-checks">
      {st.checks.map((d, i) => <li key={i} data-testid={`fmc-check-${i}`}>Perlu dicek: {d.msg}</li>)}</ul>}
    {(st.notes || []).map((n, i) => <div key={i} className="muted" data-testid={`fmc-note-${i}`}>{n}</div>)}
  </div>;
}

export function FormCheckFix({ job, disabled, onFix }) {
  const [st, reload] = useFormCheck(job);
  const [ask, setAsk] = useState(false);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const go = async (r) => { setBusy(true); try { await onFix(r); setAsk(false); setReason(""); } finally { setBusy(false); reload(); } };
  const click = () => (needsFixReason(st) ? setAsk(true) : go(""));
  return <div style={{ flexBasis: "100%", display: "flex", flexDirection: "column", gap: 6 }} data-testid="fmc-panel">
    <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
      <DesignFormButton channel={getChannel()} orderNumber={job.order_number} jobId={job.id} onSaved={reload}/>
      <button className="primary small-btn shopee-btn-fix" onClick={click} disabled={disabled || busy}
        title={disabled ? "Tambahkan minimal 1 mockup sebelum Tandai FIX" : undefined} data-testid="shopee-detail-fix">
        <CheckCircle2 size={14}/> Tandai FIX</button>
    </div>
    {st?.state !== "off" && <CheckStatus st={st}/>}
    {ask && <div className="warn-box small" style={{ margin: 0 }} data-testid="fmc-reason-box">
      <div>Pengecualian — tinjau hasil di atas. Alasan, nama Anda, dan waktu dicatat dan dapat dilihat Owner.</div>
      <textarea rows={2} value={reason} onChange={e => setReason(e.target.value)} style={{ width: "100%", marginTop: 4 }}
        placeholder="Alasan tetap FIX (min. 5 karakter)" data-testid="fmc-reason"/>
      <div style={{ display: "flex", gap: 6, marginTop: 4 }}>
        <button className="primary small-btn" disabled={busy || reason.trim().length < 5} onClick={() => go(reason.trim())}
          data-testid="fmc-fix-with-reason">Tetap FIX dengan Alasan</button>
        <button className="soft-btn small-btn" onClick={() => setAsk(false)} data-testid="fmc-reason-cancel">Batal</button>
      </div>
    </div>}
  </div>;
}
